import { oc } from "@orpc/contract";
import { z } from "zod";

/** Lifecycle of an authored config. */
export const sd849ConfigStatusSchema = z.enum(["draft", "shadow", "active"]);

/** A SupplierConfig travels as plain JSON; the backend validates it strictly. */
const configObjectSchema = z.record(z.string(), z.unknown());

const validationSchema = z.object({
  valid: z.boolean(),
  errors: z.array(z.string()),
});

export const sd849AuthoredConfigSchema = z.object({
  id: z.uuid(),
  supplierName: z.string(),
  /** Variant discriminator (default "default"). */
  label: z.string(),
  supplierId: z.uuid().nullable(),
  config: configObjectSchema,
  status: sd849ConfigStatusSchema,
  notes: z.string().nullable(),
  updatedBy: z.uuid().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const sd849SupplierConfigSummarySchema = z.object({
  supplierName: z.string(),
  status: sd849ConfigStatusSchema.nullable(),
  notes: z.string().nullable(),
  updatedAt: z.string().nullable(),
  hasAuthored: z.boolean(),
});

export const listSd849ConfigSuppliersResponseSchema = z.object({
  suppliers: z.array(sd849SupplierConfigSummarySchema),
});

export const getSd849ConfigResponseSchema = z.object({
  supplierName: z.string(),
  authored: sd849AuthoredConfigSchema.nullable(),
});

export const saveSd849ConfigInputSchema = z.object({
  supplierName: z.string().trim().min(1),
  /** Variant to upsert; defaults to "default" (the single-variant case). */
  label: z.string().trim().min(1).optional(),
  config: configObjectSchema,
  /**
   * `updatedAt` of the authored row this edit was based on. When supplied, the
   * server refuses the write if the row moved since — the whole config is
   * replaced here, so a save built on a stale read silently reverts whoever
   * wrote in between. Omit only when there is no stored row yet.
   */
  expectedUpdatedAt: z.string().optional(),
  // No `status`: only `draft` is implemented. Authored configs do not yet
  // override the live parser, so accepting shadow/active would misrepresent
  // reality. Status is (re)introduced with the shadow→active rollout.
  notes: z.string().max(2000).optional(),
});

export const saveSd849ConfigResponseSchema = z.object({
  saved: sd849AuthoredConfigSchema.nullable(),
  validation: validationSchema,
  /** True when the write was refused because the stored row moved (not a validation failure). */
  conflict: z.boolean().optional(),
});

export const testSd849ConfigInputSchema = z.object({
  sampleId: z.uuid(),
  config: configObjectSchema,
});

export const testSd849ConfigResponseSchema = z.object({
  validation: validationSchema,
  ran: z.boolean(),
  reason: z.string().nullable(),
  coverage: configObjectSchema.nullable(),
  warnings: z.array(z.string()),
});

/** Identifies one authored config: the supplier plus its variant label. */
const supplierConfigRefSchema = z.object({
  supplierName: z.string().trim().min(1),
  label: z.string().trim().min(1).optional(),
});

const listSuppliersContract = oc
  .input(z.object({}))
  .output(listSd849ConfigSuppliersResponseSchema)
  .route({
    method: "GET",
    summary: "List suppliers with a committed or authored 849 parsing config",
  });

const getConfigContract = oc
  .input(supplierConfigRefSchema)
  .output(getSd849ConfigResponseSchema)
  .route({
    method: "GET",
    summary: "Get the authored + committed 849 config for a supplier",
  });

const validateConfigContract = oc
  .input(z.object({ config: configObjectSchema }))
  .output(validationSchema)
  .route({ summary: "Validate a draft 849 config against the schema" });

const saveConfigContract = oc
  .input(saveSd849ConfigInputSchema)
  .output(saveSd849ConfigResponseSchema)
  .route({ summary: "Validate and save an authored 849 config (draft store)" });

/** One append-only snapshot of a config. */
export const sd849ConfigVersionSchema = z.object({
  id: z.uuid(),
  versionNumber: z.int().positive(),
  config: configObjectSchema,
  status: sd849ConfigStatusSchema,
  notes: z.string().nullable(),
  changeReason: z.enum(["save", "rollback", "promote"]).nullable(),
  createdBy: z.uuid().nullable(),
  createdAt: z.string(),
});

export const listSd849ConfigVersionsResponseSchema = z.object({
  versions: z.array(sd849ConfigVersionSchema),
});

export const rollbackSd849ConfigInputSchema = supplierConfigRefSchema.extend({
  versionNumber: z.int().positive(),
});

const listVersionsContract = oc
  .input(supplierConfigRefSchema)
  .output(listSd849ConfigVersionsResponseSchema)
  .route({
    method: "GET",
    summary: "Version history for an authored 849 config (newest first)",
  });

const rollbackConfigContract = oc
  .input(rollbackSd849ConfigInputSchema)
  .output(saveSd849ConfigResponseSchema)
  .route({
    summary:
      "Restore a previous 849 config version (appends a new version; status unchanged)",
  });

const testConfigContract = oc
  .input(testSd849ConfigInputSchema)
  .output(testSd849ConfigResponseSchema)
  .route({
    summary: "Dry-run a draft 849 config against a stored sample email",
  });

export const checkSenderClaimsInputSchema = z.object({
  /** Sender addresses (or patterns) being considered for a supplier's config. */
  addresses: z.array(z.string().trim().min(1)).max(50),
  /** The supplier being edited — excluded from its own conflict list. */
  supplierName: z.string().trim().min(1),
});

export const senderClaimSchema = z.object({
  address: z.string(),
  /** Formal names of OTHER effective configs whose patterns match this address. */
  claimedBy: z.array(z.string()),
});

export const checkSenderClaimsResponseSchema = z.object({
  claims: z.array(senderClaimSchema),
});

export const setSenderPatternsInputSchema = z.object({
  supplierName: z.string().trim().min(1),
  label: z.string().trim().min(1).optional(),
  /** Replaces `emailDetection.patterns` wholesale; merged server-side. */
  patterns: z.array(z.string().trim().min(1)).max(50),
  /**
   * `updatedAt` of the row this edit was based on. Required: server-side merging
   * protects the OTHER fields, but these patterns are still replaced wholesale, so
   * a stale draft would revert a newer routing change.
   */
  expectedUpdatedAt: z.string().optional(),
});

const setSenderPatternsContract = oc
  .input(setSenderPatternsInputSchema)
  .output(saveSd849ConfigResponseSchema)
  .route({
    summary:
      "Replace a supplier's 849 sender addresses (field-scoped, merged server-side)",
  });

const checkSenderClaimsContract = oc
  .input(checkSenderClaimsInputSchema)
  .output(checkSenderClaimsResponseSchema)
  .route({
    summary:
      "Which other 849 configs already claim these sender addresses (read-only)",
  });

export const sd849ConfigsContracts = {
  listSuppliers: listSuppliersContract,
  getConfig: getConfigContract,
  validate: validateConfigContract,
  save: saveConfigContract,
  listVersions: listVersionsContract,
  rollback: rollbackConfigContract,
  testAgainstSample: testConfigContract,
  checkSenderClaims: checkSenderClaimsContract,
  setSenderPatterns: setSenderPatternsContract,
};

export type Sd849ConfigStatus = z.infer<typeof sd849ConfigStatusSchema>;
export type Sd849AuthoredConfig = z.infer<typeof sd849AuthoredConfigSchema>;
export type Sd849SupplierConfigSummary = z.infer<
  typeof sd849SupplierConfigSummarySchema
>;
export type ListSd849ConfigSuppliersResponse = z.infer<
  typeof listSd849ConfigSuppliersResponseSchema
>;
export type GetSd849ConfigResponse = z.infer<
  typeof getSd849ConfigResponseSchema
>;
export type SaveSd849ConfigInput = z.infer<typeof saveSd849ConfigInputSchema>;
export type SaveSd849ConfigResponse = z.infer<
  typeof saveSd849ConfigResponseSchema
>;
export type TestSd849ConfigResponse = z.infer<
  typeof testSd849ConfigResponseSchema
>;
export type Sd849ConfigVersion = z.infer<typeof sd849ConfigVersionSchema>;
export type ListSd849ConfigVersionsResponse = z.infer<
  typeof listSd849ConfigVersionsResponseSchema
>;
export type RollbackSd849ConfigInput = z.infer<
  typeof rollbackSd849ConfigInputSchema
>;
export type SetSenderPatternsInput = z.infer<
  typeof setSenderPatternsInputSchema
>;
export type CheckSenderClaimsInput = z.infer<
  typeof checkSenderClaimsInputSchema
>;
export type SenderClaim = z.infer<typeof senderClaimSchema>;
export type CheckSenderClaimsResponse = z.infer<
  typeof checkSenderClaimsResponseSchema
>;
